/**
 * Offering data from nivaas API
 */
export interface NivaasOffering {
  __typename: 'RoomOffering';
  id: string;
  name: string;
  slug: string;
  capacity: number;
  inventory: number;
  description: string;
  pricing: Array<{
    unitPrice: {
      amount: number;
      currency: string;
    };
    gst: number;
    applicableFromDate: string;
    applicableOnDates?: string[] | null;
    applicableUntilDate?: string | null;
  } | null>;
}

/**
 * Get unit price from an offering (per-person price)
 */
export function getOfferingDisplayPrice(
  offering: NivaasOffering | null | undefined,
): { amount: number; currency: string; gst: number } | null {
  const price = offering?.pricing[0]?.unitPrice;
  if (!price) return null;

  return {
    amount: price.amount,
    currency: price.currency,
    gst: offering?.pricing[0]?.gst || 0,
  };
}

/**
 * Get full room price from an offering (unit_price * capacity)
 * This is what customers actually pay per night for a room.
 * Use this for display purposes to avoid confusion.
 */
export function getOfferingFullRoomPrice(
  offering: NivaasOffering | null | undefined,
): { amount: number; currency: string; gst: number; capacity: number } | null {
  const price = offering?.pricing[0]?.unitPrice;
  if (!price) return null;

  const capacity = offering?.capacity ?? 1;
  return {
    amount: price.amount * capacity,
    currency: price.currency,
    gst: offering?.pricing[0]?.gst || 0,
    capacity,
  };
}

/**
 * Get the current price for an offering based on a specific date
 * Returns the most specific applicable pricing strategy
 */
export function getOfferingPriceForDate(
  offering: NivaasOffering | null | undefined,
  date?: string,
): { amount: number; currency: string; gst: number } | null {
  if (!offering?.pricing || offering.pricing.length === 0) {
    return null;
  }

  const targetDate = date ? new Date(date) : new Date();

  // Filter pricing strategies applicable to the target date
  const applicablePricing = offering.pricing
    .filter((p): p is NonNullable<typeof p> => p !== null)
    .filter((p) => {
      const fromDate = new Date(p.applicableFromDate);
      const untilDate = p.applicableUntilDate ? new Date(p.applicableUntilDate) : null;

      // Check if date is within the range
      if (targetDate < fromDate) return false;
      if (untilDate && targetDate > untilDate) return false;

      // If specific dates are defined, check if target date is in that list
      if (p.applicableOnDates && p.applicableOnDates.length > 0) {
        const targetDateStr = targetDate.toISOString().split('T')[0];
        return p.applicableOnDates.includes(targetDateStr);
      }

      return true;
    });

  if (applicablePricing.length === 0) {
    return null;
  }

  // Prefer specific date pricing over range pricing
  const specificDatePricing = applicablePricing.find(
    (p) => p.applicableOnDates && p.applicableOnDates.length > 0,
  );

  const selectedPricing = specificDatePricing || applicablePricing[0];

  return {
    amount: selectedPricing.unitPrice.amount,
    currency: selectedPricing.unitPrice.currency,
    gst: selectedPricing.gst,
  };
}

/**
 * Format price for display
 */
export function formatPrice(price: { amount: number; currency: string } | null): string {
  if (!price) return 'Contact for pricing';

  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  return formatter.format(price.amount);
}
